import React, { useState, useEffect } from "react";
import Select from "react-select";

const ItemEntryPopup = ({ 
  isOpen, 
  onClose, 
  onSave, 
  itemOptions, 
  title = "Add Item",
  initialData = null 
}) => {
  const [itemData, setItemData] = useState({
    item_id: null,
    qty: "",
    rate: "",
    total_amount: 0
  });

  const [errors, setErrors] = useState({});

  // Reset form when popup opens/closes or initialData changes
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setItemData(initialData);
      } else {
        setItemData({
          item_id: null,
          qty: "",
          rate: "",
          total_amount: 0
        });
      }
      setErrors({});
    }
  }, [isOpen, initialData]);

  // Calculate total amount when qty or rate changes
  useEffect(() => {
    const qty = parseFloat(itemData.qty || 0) || 0;
    const rate = parseFloat(itemData.rate || 0) || 0;
    const total = qty * rate;
    
    setItemData(prev => ({
      ...prev,
      total_amount: total
    }));
  }, [itemData.qty, itemData.rate]);

  const formatCurrency = (amount) => {
    const val = Number(amount) || 0;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    })
      .format(val)
      .replace("₹", "₹ ");
  };

  const handleInputChange = (field, value) => {
    setItemData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!itemData.item_id) {
      newErrors.item_id = "Please select an item";
    }
    
    if (!itemData.qty || parseFloat(itemData.qty) <= 0) {
      newErrors.qty = "Please enter a valid quantity";
    }
    
    if (!itemData.rate || parseFloat(itemData.rate) <= 0) {
      newErrors.rate = "Please enter a valid rate";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave({
        ...itemData,
        item_id: Number(itemData.item_id)
      });
      onClose();
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal" style={{ 
      display: 'block', 
      backgroundColor: 'rgba(0,0,0,0.5)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1050
    }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={handleClose}
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          
          <div className="modal-body">
            <div className="form-group mb-3">
              <label>Select Item <span className="text-danger">*</span></label>
              <Select
                options={itemOptions}
                value={itemOptions.find(opt => opt.value === itemData.item_id) || null}
                onChange={(opt) => handleInputChange('item_id', opt ? opt.value : null)}
                placeholder="--Select Item--"
                className={errors.item_id ? 'is-invalid' : ''}
              />
              {errors.item_id && <div className="invalid-feedback d-block">{errors.item_id}</div>}
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label>Quantity <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control ${errors.qty ? 'is-invalid' : ''}`}
                    value={itemData.qty}
                    onChange={(e) => handleInputChange('qty', e.target.value)}
                    min="0"
                    step="0.01"
                    placeholder="Enter quantity"
                  />
                  {errors.qty && <div className="invalid-feedback">{errors.qty}</div>}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label>Rate <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control ${errors.rate ? 'is-invalid' : ''}`}
                    value={itemData.rate}
                    onChange={(e) => handleInputChange('rate', e.target.value)}
                    min="0"
                    step="0.01"
                    placeholder="Enter rate"
                  />
                  {errors.rate && <div className="invalid-feedback">{errors.rate}</div>}
                </div>
              </div>
            </div>

            <div className="form-group mb-3">
              <label>Total Amount</label>
              <input
                type="text"
                className="form-control"
                value={formatCurrency(itemData.total_amount)}
                readOnly
                style={{ backgroundColor: '#f8f9fa' }}
              />
            </div>
          </div>
          
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={handleClose}
            >
              Cancel
            </button>
            <button 
              type="button" 
              className="btn btn-primary" 
              onClick={handleSave}
            >
              Save Item
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemEntryPopup;